// ============================================================================
// gyorsitotar.js — a Firestore helyi gyorsítótárának döntései (2026-10-08, költség).
//
// MIÉRT: a Firestore minden olvasott dokumentumot számláz. Alapértelmezetten a webes
// SDK csak MEMÓRIA-gyorsítótárat használ, és amint egy figyelő leáll, a hozzá tartozó
// dokumentumokat el is felejti: ha a tag szobát vált és visszatér, a szoba legutóbbi 50
// üzenetét MINDEN alkalommal újra kifizetjük (50 olvasás), akkor is, ha semmi nem
// változott. TARTÓS (IndexedDB) gyorsítótárral az SDK megjegyzi a lekérdezés utolsó
// állapotát, és újrafeliratkozáskor - a Firestore dokumentációja szerint, ha a legutóbbi
// állapot 30 percnél frissebb - csak a VÁLTOZÁSOKAT számlázzák.
//
// Ez a modul a tiszta döntéseket tartalmazza (Node-ban tesztelhető); a bekötés a
// firebaseClient.js-ben van.
// ============================================================================

/** Van-e (szokásos) hálózat? Ismeretlen környezetben "van"-nak vesszük (a régi viselkedés). */
export function halozatVan(nav = typeof navigator !== "undefined" ? navigator : null) {
  try {
    return !nav || nav.onLine !== false;
  } catch (e) {
    return true;
  }
}

/** Elérhető-e tartós tároló (IndexedDB)? Privát böngészésben / letiltva nem. */
export function tartosTaroloLehetseges(win = typeof globalThis !== "undefined" ? globalThis : {}) {
  try {
    return typeof win.indexedDB !== "undefined" && win.indexedDB !== null;
  } catch (e) {
    return false;
  }
}

/**
 * Eldobjuk-e ezt az élő figyelő-pillanatképet?
 *
 * Tartós gyorsítótárral az SDK az ELSŐ pillanatképet azonnal a helyi tárból adja
 * (metadata.fromCache === true), és csak utána jön a szerver állapota. A chat az első
 * betöltést "meglévő előzménynek" tekinti (nem jelez róla, ott számolja ki a "Meddig
 * olvastad" elválasztót) - egy elavult, gyorsítótárból jövő első pillanatkép ezt
 * elrontaná: a tárolt állapot után érkező üzenetek "újnak" látszanának, és hangot /
 * értesítést váltanának ki, a hiányzó olvasatlan-elválasztó pedig rossz helyre kerülne.
 *
 * ezért: hálózattal (online) a gyorsítótárból jövő pillanatképet NEM adjuk tovább, csak
 * a szerverrel egyeztetettet. A megtakarítás ettől nem változik (az olvasást az számlázza,
 * hogy a szerver mit küld, nem az, hogy mit mutatunk). Kapcsolat nélkül (offline) viszont
 * a gyorsítótár az egyetlen forrás - azt megmutatjuk.
 */
export function gyorsitotarbolJovoEldobando(snap, online = halozatVan()) {
  return Boolean(snap && snap.metadata && snap.metadata.fromCache === true && online);
}

// ----------------------------------------------------------------------------
// FELHASZNÁLÓHOZ KÖTÉS (2026-10-08)
//
// A Firestore tartós gyorsítótára NEM felhasználónkénti: a tárolt dokumentumokhoz nem tartozik
// "tulajdonos". Közös eszközön a következő belépő az előző tag üzeneteit, cetlijeit látná, ha a
// kijelentkezéskori törlés elmaradt (lejárt munkamenet, bezárt lap, a törlés hibája).
//
// Ezért megjegyezzük, KIÉ a tárolt adat (a localStorage-ban, eszközönként): belépéskor, az első
// Firestore-hívás ELŐTT összevetjük a belépővel, és eltérésnél (vagy ha nincs jelölés) töröljük a
// gyorsítótárat. A jelölést csak SIKERES törlés után írjuk be - így egy elbukott törlés a következő
// belépéskor újra próbálkozik, és nem marad "tiszta" jelzés tiszta tár nélkül.
// ----------------------------------------------------------------------------
export const TULAJDONOS_KULCS = "lineage0_fs_cache_owner";

function tarolo(storage) {
  if (storage) return storage;
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch (e) {
    return null; // letiltott tároló: nincs jelölés, tehát minden belépéskor törlünk (biztonságos irány)
  }
}

export function tulajdonosOlvas(storage) {
  try {
    const s = tarolo(storage);
    return s ? s.getItem(TULAJDONOS_KULCS) : null;
  } catch (e) {
    return null;
  }
}

export function tulajdonosIr(uid, storage) {
  try {
    const s = tarolo(storage);
    if (s) s.setItem(TULAJDONOS_KULCS, uid);
  } catch (e) {
    /* nem kritikus: jelölés nélkül a következő belépéskor törlünk */
  }
}

export function tulajdonosTorol(storage) {
  try {
    const s = tarolo(storage);
    if (s) s.removeItem(TULAJDONOS_KULCS);
  } catch (e) {
    /* nem kritikus */
  }
}

/** A tárolt jelölés pontosan ennek a felhasználónak szól? (hiányzó / üres jelölés: nem) */
export function tulajdonosEgyezik(tarolt, uid) {
  return typeof tarolt === "string" && tarolt.length > 0 && typeof uid === "string" && uid.length > 0 && tarolt === uid;
}

/**
 * A gyorsítótár gazdájának ellenőrzése belépéskor (az első Firestore-hívás ELŐTT, vagy - ha egy korábbi
 * munkamenet után ugyanabban a lapban lép be más - a Firestore leállításával). A törlést és az újratöltést
 * a hívó adja (`torol`, `leallitasEsTorles`, `ujratoltes`), így a döntés Node-ban tesztelhető.
 *
 * KIJELENTKEZÉSKOR A GYORSÍTÓTÁR MEGMARAD (2026-10-08, tagi kérésre): ugyanaz a tag újra belépve tovább
 * használja (ez adja a megtakarítást). Csak MÁS felhasználó belépésekor törlődik, mielőtt bármit látna.
 *
 * @returns {Promise<"kihagyva"|"egyezik"|"torolve"|"ujratoltve"|"hiba">}
 *   kihagyva:   memória-gyorsítótár (nincs mit őrizni);
 *   egyezik:    a tárolt adat ennek a felhasználónak szól: marad;
 *   torolve:    eltérő / ismeretlen tulajdonos, a Firestore még nem indult: kitöröltük, mostantól a belépőé;
 *   ujratoltve: eltérő tulajdonos, de a lapban már futott a Firestore (az előző tag munkamenete): leállítottuk,
 *               töröltük, és újratöltünk (a leállított példány nem használható tovább);
 *   hiba:       a törlés elbukott - a jelölés NEM íródott be, a következő belépés újra próbálja.
 */
export async function gazdaEllenorzes({
  tartosAktiv, firestoreHasznalva, uid, torol, leallitasEsTorles, ujratoltes, storage, figyelmeztet,
}) {
  if (!tartosAktiv) return "kihagyva";
  if (tulajdonosEgyezik(tulajdonosOlvas(storage), uid)) return "egyezik";
  const jelez = (e) => { if (typeof figyelmeztet === "function") figyelmeztet(e); };

  if (firestoreHasznalva) {
    // Az előző tag munkamenete után ugyanabban a lapban lépett be más: a futó Firestore-példány gyorsítótára
    // még az előző tagé. Leállítás + törlés + újratöltés (a leállított példány nem használható tovább).
    try {
      await leallitasEsTorles();
      tulajdonosIr(uid, storage);
    } catch (e) {
      jelez(e); // a jelölés marad a régi: az újratöltés utáni belépés újra próbálkozik
    }
    if (typeof ujratoltes === "function") ujratoltes();
    return "ujratoltve";
  }

  try {
    await torol();
    tulajdonosIr(uid, storage);
    return "torolve";
  } catch (e) {
    jelez(e);
    return "hiba";
  }
}

// ----------------------------------------------------------------------------
// A SZŰRŐ HELYES BEKÖTÉSE (2026-10-08, hibajavítás)
//
// HIBA, AMI ELŐFORDULT: a `gyorsitotarbolJovoEldobando` önmagában kevés volt. Az onSnapshot alapból
// CSAK akkor hív vissza, ha az ADAT változott. Ha a lekérdezés eredménye a gyorsítótárból jött
// (fromCache: true), majd a szerver "ugyanez az állapot" visszaigazolást küld (fromCache: false, adat
// nem változott), az SDK ezt a metaadat-váltást NEM jelzi - a kezelő addig nem kap semmit. A korábbi
// szűrő az egyetlen (gyorsítótáras) pillanatképet eldobta, így a szobalista (és az üzenetek) üresek
// maradtak: "Még nincs egy szoba sem".
//
// JAVÍTÁS: (1) az onSnapshot `includeMetadataChanges: true` kapcsolóval fut, így a szerver-visszaigazolás is
// pillanatképként jön; (2) a visszatartott gyorsítótáras pillanatképet időkorlát után MINDEN ESETRE
// továbbadjuk (lassú / akadozó hálózaton se maradjon üres a felület - a régi viselkedésnél nem rosszabb).
// ----------------------------------------------------------------------------

/**
 * Szűrő egy élő figyelőhöz: online csak a szerverrel egyeztetett pillanatképet adja tovább, a
 * gyorsítótárból jövőt visszatartja - de `idokorlatMs` után, ha szerver-visszaigazolás nem jött, azt is.
 *
 * @param {(snap:any)=>void} tovabb  a valódi kezelő
 * @param {{idokorlatMs?:number, online?:()=>boolean, timers?:{setTimeout:Function, clearTimeout:Function}}} [opts]
 * @returns {((snap:any)=>void) & {leallit:()=>void}}
 */
export function kezbesitoSzuro(tovabb, opts = {}) {
  const idokorlatMs = typeof opts.idokorlatMs === "number" ? opts.idokorlatMs : 5000;
  const online = typeof opts.online === "function" ? opts.online : halozatVan;
  const t = opts.timers || { setTimeout: (f, ms) => setTimeout(f, ms), clearTimeout: (h) => clearTimeout(h) };
  let varakozo = null;
  let ora = null;
  let voltTovabbitas = false;

  function leallit() {
    if (ora !== null) { t.clearTimeout(ora); ora = null; }
    varakozo = null;
  }

  // Csak metaadat-váltás (az adat nem változott)? Az első továbbadott pillanatkép után az ilyet eldobjuk:
  // a chat kezelője minden további pillanatképet "új érkezésként" kezelhet (hang / értesítés).
  function csakMetaadat(snap) {
    try {
      return typeof snap.docChanges === "function" && snap.docChanges().length === 0;
    } catch (e) {
      return false;
    }
  }

  // v181: ha az utoljára továbbadott pillanatkép ÜRES és a gyorsítótárból jött (igazolatlan), akkor a szerver "ugyanez az üres állapot"
  // visszaigazolását is továbbadjuk - különben a nézet örökre "igazolatlan" jelzésnél maradna (az üres lista csak ettől lesz igazolt).
  let utolsoIgazolatlanUres = false;
  const ures = (snap) => (snap && snap.docs ? snap.docs.length === 0 : snap && snap.empty === true);
  const gyorsitotarbol = (snap) => Boolean(snap && snap.metadata && snap.metadata.fromCache === true);

  function tovabbad(snap) {
    voltTovabbitas = true;
    utolsoIgazolatlanUres = gyorsitotarbol(snap) && ures(snap);
    tovabb(snap);
  }

  function kezel(snap) {
    if (!gyorsitotarbolJovoEldobando(snap, online())) {
      // Szerverrel egyeztetett (vagy offline gyorsítótár): a visszatartott régebbi pillanatkép elavult.
      if (ora !== null) { t.clearTimeout(ora); ora = null; }
      varakozo = null;
      const ureshezIgazolas = utolsoIgazolatlanUres && !gyorsitotarbol(snap);
      if (voltTovabbitas && csakMetaadat(snap) && !ureshezIgazolas) return; // ismétlés (pl. a szerver visszaigazolta ugyanazt)
      tovabbad(snap);
      return;
    }
    if (voltTovabbitas && csakMetaadat(snap)) return; // újabb metaadat-váltás ugyanarról az adatról
    varakozo = snap;
    if (ora === null) {
      ora = t.setTimeout(() => {
        ora = null;
        const v = varakozo;
        varakozo = null;
        if (v) tovabbad(v);
      }, idokorlatMs);
    }
  }
  kezel.leallit = leallit;
  return kezel;
}
